// Local-first training video: one copy saved on this laptop in IndexedDB (downloaded once in Range chunks with retry and
// resume, or a file the host picks). Singleton shared by the stage and /lab.
import { outbox } from './outbox';

const BASE = process.env.REACT_APP_BACKEND_URL;
const DB = 'spore-media';
const STORE = 'kv';
const KEY = 'training';
const CHUNK = 6 * 1024 * 1024;
const MB = 1e6;

let dbp = null;
const db = () =>
  dbp ||
  (dbp = new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  }));
const op = async (mode, fn) => {
  const d = await db();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, mode);
    const r = fn(tx.objectStore(STORE));
    tx.oncomplete = () => res(r && r.result);
    tx.onerror = () => rej(tx.error);
    tx.onabort = () => rej(tx.error);
  });
};
const get = (k) => op('readonly', (s) => s.get(k));
const put = (k, v) => op('readwrite', (s) => s.put(v, k));
const del = (k) => op('readwrite', (s) => s.delete(k));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let paused = false; // the show is running: no background download competing for bandwidth / CPU
const whilePaused = async () => {
  while (paused) await sleep(500);
};

const st = { local: null, url: null, persisted: null, dl: { status: 'idle', bytes: 0, total: 0, speed: 0, eta: 0, error: null, variant: null } };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
let initP = null;
let running = null;

const setLocal = (rec) => {
  if (st.url) URL.revokeObjectURL(st.url);
  st.url = rec ? URL.createObjectURL(rec.blob) : null;
  st.local = rec ? { origin: rec.origin, name: rec.name, size: rec.size, type: rec.type, variant: rec.variant, savedAt: rec.savedAt, stored: true } : null;
};
const persist = async () => {
  try {
    st.persisted = navigator.storage && navigator.storage.persist ? await navigator.storage.persist() : null;
  } catch (e) {
    st.persisted = false;
  }
};
const canPlay = (type) => document.createElement('video').canPlayType(type);

async function fetchRange(url, start, end) {
  let err;
  for (let a = 0; a < 6; a += 1) {
    try {
      const r = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, priority: 'low', cache: 'no-store' });
      if (r.status !== 206) throw new Error(`HTTP ${r.status} (expected 206)`);
      const b = await r.blob();
      if (b.size !== end - start + 1) throw new Error(`short chunk ${b.size}/${end - start + 1}`);
      return b;
    } catch (e) {
      err = e;
      st.dl.error = `retry ${a + 1}: ${e.message}`;
      emit();
      await sleep(1000 * 2 ** a);
    }
  }
  throw err;
}

async function runDownload() {
  const dl = st.dl;
  Object.assign(dl, { status: 'downloading', error: null, speed: 0, eta: 0 });
  emit();
  const r0 = await fetch(`${BASE}/api/media/training/info`);
  if (!r0.ok) throw new Error(`info HTTP ${r0.status}`);
  const info = await r0.json();
  const src = (info.sources || []).filter((s) => s.variant !== 'original' && canPlay(s.type)).sort((a, b) => a.size - b.size)[0];
  if (!src) throw new Error('no playable source to save');
  const url = `${BASE}${src.url}`;
  let part = await get('partial');
  if (!part || part.variant !== src.variant || part.total !== src.size || part.chunk !== CHUNK) {
    for (let i = 0; part && i < part.count; i += 1) await del(`part:${i}`);
    part = { variant: src.variant, total: src.size, chunk: CHUNK, count: 0, mime: src.type.split(';')[0] };
    await put('partial', part);
  }
  const n = Math.ceil(part.total / CHUNK);
  Object.assign(dl, { variant: src.variant, total: part.total, bytes: Math.min(part.count * CHUNK, part.total) });
  emit();
  for (let i = part.count; i < n; i += 1) {
    if (paused) {
      Object.assign(dl, { status: 'paused' });
      emit();
      await whilePaused();
      Object.assign(dl, { status: 'downloading' });
    }
    const t0 = performance.now();
    const start = i * CHUNK;
    const b = await fetchRange(url, start, Math.min(start + CHUNK, part.total) - 1);
    await put(`part:${i}`, b);
    part.count = i + 1;
    await put('partial', part);
    const s = b.size / ((performance.now() - t0) / 1000);
    dl.speed = dl.speed ? dl.speed * 0.6 + s * 0.4 : s;
    dl.bytes = Math.min(part.count * CHUNK, part.total);
    dl.eta = (part.total - dl.bytes) / dl.speed;
    dl.error = null;
    emit();
  }
  const parts = [];
  for (let i = 0; i < n; i += 1) parts.push(await get(`part:${i}`));
  const blob = new Blob(parts, { type: part.mime });
  if (blob.size !== part.total) throw new Error(`assembled ${blob.size}/${part.total} bytes`);
  const rec = { blob, origin: 'download', name: `training.${src.variant === 'webm' ? 'webm' : 'mp4'}`, size: blob.size, type: part.mime, variant: src.variant, savedAt: new Date().toISOString() };
  await put(KEY, rec);
  for (let i = 0; i < n; i += 1) await del(`part:${i}`);
  await del('partial');
  await persist();
  setLocal(rec);
  Object.assign(dl, { status: 'done', eta: 0 });
  emit();
}

export const localVideo = {
  state: () => ({ ...st, paused, dl: { ...st.dl } }),
  pause() {
    paused = true;
  },
  resume() {
    paused = false;
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  init() {
    if (!initP)
      initP = (async () => {
        try {
          const rec = await get(KEY);
          if (rec && rec.blob) setLocal(rec);
          const part = await get('partial');
          if (!rec && part) Object.assign(st.dl, { variant: part.variant, total: part.total, bytes: Math.min(part.count * part.chunk, part.total) });
          if (navigator.storage && navigator.storage.persisted) st.persisted = await navigator.storage.persisted();
        } catch (e) {
          st.dl.error = `IndexedDB: ${e.message || e}`;
        }
        emit();
      })();
    return initP;
  },
  download({ force = false } = {}) {
    if (running) return running;
    running = (async () => {
      await localVideo.init();
      if (force) await localVideo.remove();
      if (st.local) return;
      try {
        await runDownload();
      } catch (e) {
        Object.assign(st.dl, { status: 'error', error: String(e.message || e) });
        emit();
      }
    })().finally(() => {
      running = null;
    });
    return running;
  },
  // the landing page: fetch once per laptop, in the background
  async autoSave() {
    await localVideo.init();
    if (!st.local && st.dl.status === 'idle') localVideo.download();
  },
  async remove() {
    await del(KEY);
    const part = await get('partial');
    for (let i = 0; part && i < part.count; i += 1) await del(`part:${i}`);
    await del('partial');
    setLocal(null);
    Object.assign(st.dl, { status: 'idle', bytes: 0, total: 0, speed: 0, eta: 0, error: null });
    emit();
  },
  // plays immediately from memory (zero server calls), then persists to IndexedDB and verifies the stored copy
  async useFile(file) {
    const rec = { blob: file, origin: 'file', name: file.name, size: file.size, type: file.type || 'video/mp4', variant: 'file', savedAt: new Date().toISOString() };
    setLocal(rec);
    st.fileError = null;
    emit();
    try {
      await put(KEY, rec);
      const back = await get(KEY);
      if (!back || !back.blob || back.blob.size !== file.size) throw new Error(`stored copy is ${back && back.blob ? back.blob.size : 0}/${file.size} bytes`);
      await persist();
      st.local.stored = true;
    } catch (e) {
      st.fileError = `NOT KEPT AFTER RELOAD · IndexedDB: ${e.message || e}`;
      outbox.post('/api/client-log', { kind: 'idb', name: 'useFile', message: st.fileError, source: file.name, size: file.size, ua: navigator.userAgent, at: new Date().toISOString() });
    }
    emit();
  },
};

export const fmtMB = (b) => `${(b / MB).toFixed(b >= 10 * MB ? 0 : 1)} MB`;
